import { translationBaseKey } from '../../../decorators/translation-base-key.decorator';
import { CalculationRunOptions } from '../calculation-run.options';
import { advancedFormControl, FieldType, FormControlTypeEnum } from '@magieno/common';
import { CalculationKeynameEnum } from '../../../enums/calculations/calculation-keyname.enum';
import { FieldTypeEnum } from '../../../enums/field-type.enum';
import {
  IsBoolean,
  IsEnum,
  IsInt,
  IsNotEmpty,
  IsNumber, IsOptional,
  IsString,
  MetadataKeynameEnum,
  validateIf,
} from '@pristine-ts/class-validator';
import { ErrorMessageBuilder } from '../../../builders/error-message.builder';
import {
  IncomeFrequencyPeriodEnum,
} from '../../../enums/calculations/salary-contributions/income-frequency-period.enum';
import { CivilStatusEnum } from '../../../enums/civil-status.enum';
import { clonePropertyMetadata } from '@pristine-ts/metadata';
import { SalaryContributionsOptions } from '../salary-contributions/salary-contributions.options';
import { PayFrequencyEnum } from '../../../enums/calculations/salary-contributions/pay-frequency.enum';
import { CalculationPeriodEnum } from '../../../enums/calculations/salary-contributions/calculation-period.enum';

@translationBaseKey('withholding-tax-options')
export class WithholdingTaxOptions extends CalculationRunOptions implements Omit<SalaryContributionsOptions,
  'birthdate' |
  'isTaxpayerDead' |
  'deathdate' |
  'employmentIncomeForEI'
> {
  @advancedFormControl({
    type: FormControlTypeEnum.Hidden,
  })
  calculationKeyname: CalculationKeynameEnum = CalculationKeynameEnum.WithholdingTax;

  @clonePropertyMetadata<SalaryContributionsOptions>(SalaryContributionsOptions, 'employmentIncome')
  employmentIncome: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Periode associé au salaire brut',
        en: 'Frequency associated to the employment income',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Select,
    items: [
      {
        label: {
          fr: 'Annuel',
          en: 'Yearly',
        },
        id: IncomeFrequencyPeriodEnum.Yearly,
      },
      {
        label: {
          fr: 'Mensuel',
          en: 'Monthly',
        },
        id: IncomeFrequencyPeriodEnum.Monthly,
      },
      // {
      //   label: {
      //     fr: 'Horaire',
      //     en: 'Hourly',
      //   },
      //   id: IncomeFrequencyPeriodEnum.Hourly,
      // },
    ],
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsEnum(IncomeFrequencyPeriodEnum, { buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  incomeFrequencyPeriod: IncomeFrequencyPeriodEnum = IncomeFrequencyPeriodEnum.Yearly;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Année fiscale',
        en: 'Fiscal Year',
      },
      tooltip: {
        fr: 'Ce champ indique l\'année pour laquelle les calculs sont effectués.',
        en: 'This field indicates the year for which the calculations are being performed.',
      },
    },
    fieldType: FieldTypeEnum.Number,
    type: FormControlTypeEnum.Select,
    items: [
      {
        label: {
          fr: '2023',
        },
        id: 2023,
      },
      {
        label: {
          fr: '2024',
        },
        id: 2024,
      },
      {
        label: {
          fr: '2025',
        },
        id: 2025,
      },
    ],
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  fiscalYear: number = 2025;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'État civil',
        en: 'Marital Status',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Select,
    items: [
      {
        label: {
          fr: 'Célibataire',
          en: 'Single',
        },
        id: CivilStatusEnum.Single,
      },
      {
        label: {
          fr: 'Marié',
          en: 'Married',
        },
        id: CivilStatusEnum.Married,
      },
      {
        label: {
          fr: 'Conjoint de fait',
          en: 'Living Common Law',
        },
        id: CivilStatusEnum.LivingCommonLaw,
      },
      {
        label: {
          fr: 'Divorcé',
          en: 'Divorced',
        },
        id: CivilStatusEnum.Divorced,
      },
      {
        label: {
          fr: 'Veuf(ve)',
          en: 'Widowed',
        },
        id: CivilStatusEnum.Widowed,
      },
      {
        label: {
          fr: 'Séparé(e)',
          en: 'Separated',
        },
        id: CivilStatusEnum.Separated,
      },
    ],
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsEnum(CivilStatusEnum, { buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  civilStatus: CivilStatusEnum = CivilStatusEnum.Single;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Fréquence de paie',
        en: 'Pay frequency',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Select,
    items: [
      {
        label: {
          fr: 'Aux 2 semaines',
          en: 'Every 2 weeks',
        },
        id: PayFrequencyEnum.EveryTwoWeeks,
      },
      {
        label: {
          fr: 'Mensuelle',
          en: 'Monthly',
        },
        id: PayFrequencyEnum.Monthly,
      },
      {
        label: {
          fr: 'Deux fois par mois',
          en: 'Twice a month',
        },
        id: PayFrequencyEnum.TwiceAMonth,
      },
    ],
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  payFrequency: PayFrequencyEnum = PayFrequencyEnum.EveryTwoWeeks;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Calculer les cotisations pour',
        en: 'Calculate contributions for',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Select,
    items: [
      {
        label: {
          fr: `Dernière paie de l'année`,
          en: 'Last pay of the year',
        },
        id: CalculationPeriodEnum.EndOfYear,
      },
      {
        label: {
          fr: 'Cycle de paie spécifique',
          en: 'Specific Pay Cycle',
        },
        id: CalculationPeriodEnum.SpecificPayCycle,
      },
    ],
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  calculationPeriod: CalculationPeriodEnum = CalculationPeriodEnum.EndOfYear;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Cycle de paie',
        en: 'Pay Cycle',
      },
      tooltip: {
        fr: 'Représente la nième paie de l\'année',
        en: 'Represents the nth pay of the year',
      },
    },
    fieldType: FieldType.Number,
    type: FormControlTypeEnum.Input,
  })
  @IsNumber()
  @IsNotEmpty()
  @IsOptional()
  payCycle: number;

  @clonePropertyMetadata<SalaryContributionsOptions>(SalaryContributionsOptions, 'recurringPaymentRate', [
    MetadataKeynameEnum.Condition,
  ])
  @validateIf((object: WithholdingTaxOptions, root, currentPath): boolean => {
    return object.shouldCNESSTBeCalculated === true;
  })
  recurringPaymentRate?: number = 0;

  @clonePropertyMetadata<SalaryContributionsOptions>(SalaryContributionsOptions, 'shouldQPPBeCalculated')
  shouldQPPBeCalculated: boolean = true;

  @clonePropertyMetadata<SalaryContributionsOptions>(SalaryContributionsOptions, 'shouldQPIPBeCalculated')
  shouldQPIPBeCalculated: boolean = true;

  @clonePropertyMetadata<SalaryContributionsOptions>(SalaryContributionsOptions, 'shouldEIBeCalculated')
  shouldEIBeCalculated: boolean = true;

  @clonePropertyMetadata<SalaryContributionsOptions>(SalaryContributionsOptions, 'shouldCNTBeCalculated')
  shouldCNTBeCalculated: boolean = true;

  @clonePropertyMetadata<SalaryContributionsOptions>(SalaryContributionsOptions, 'shouldCNESSTBeCalculated')
  shouldCNESSTBeCalculated: boolean = true;

  @clonePropertyMetadata<SalaryContributionsOptions>(SalaryContributionsOptions, 'isEmployeeSubjectToEI')
  isEmployeeSubjectToEI: boolean = true;

  @clonePropertyMetadata<SalaryContributionsOptions>(SalaryContributionsOptions, 'shouldFSSBeCalculated')
  shouldFSSBeCalculated: boolean = false;

  @clonePropertyMetadata<SalaryContributionsOptions>(SalaryContributionsOptions, 'totalPayroll')
  @IsOptional()
  totalPayroll?: number;
}
